import * as React from 'react'

import { isPast } from 'date-fns'

import { useIsSplitEnabled } from '@buffer-mono/features'
import { useDelayedLoading, VisuallyHidden } from '@buffer-mono/popcorn'

import { ContentItemCard } from '~publish/components/ContentItemCard'
import { ContentItemCard_ContentItem } from '~publish/components/ContentItemCard/fragments/ContentItemCard_ContentItem'
import { PostCard, PostCard_Post } from '~publish/components/PostCard'
import { getRecurrenceSummary } from '~publish/components/PostCard/PostCardActions/recurrenceHelpers'
import { PostingTimeline } from '~publish/components/PostingTimeline'
import { maskFragments, readFragment } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { useTimezone } from '~publish/hooks/useTimezone'
import type { UseIntersectionObserverReturn } from '~publish/hooks/useIntersectionObserver'

import { DraftsTimeline } from './DraftsTimeline'
import { PostListError } from './PostListError'
import { PostListEmptyState, PostListSkeleton } from './PostListStates'
import { getDraftEdgeId, useDraftsList } from './useDraftsList'
import type { DraftEdge } from './useDraftsList'

import type { JSX } from 'react'

type DraftsListProps = {
  emptyState?: React.ReactNode
  showChannelNotices?: boolean
  channelIds?: string[]
  includeChannellessDrafts?: boolean
  tagIds?: string[]
  sectionHeading?: string
}

type DatedDraftEdge = DraftEdge & { date: string }
type CardRef = UseIntersectionObserverReturn[0] | null

/**
 * The drafts tab when channel-agnostic drafts are enabled: renders the
 * unified `drafts` stream (channel-specific draft posts and, when
 * `includeChannellessDrafts` is set, channel-less content items) in the
 * single order the server returns it, with one pagination cursor for the
 * whole page. `tagIds` narrows both kinds, since content items carry tags
 * too — unlike a channel or post-type filter, which only ever narrows posts.
 */
export const DraftsList = ({
  emptyState,
  showChannelNotices = false,
  channelIds,
  includeChannellessDrafts,
  tagIds,
  sectionHeading,
}: DraftsListProps): JSX.Element => {
  const isRecurringEnabled = useIsSplitEnabled('publish-recurring-posts')
  const timeZone = useTimezone()
  const { data, loading, error, lastElementRef, fetchingMore } = useDraftsList({
    channelIds,
    includeChannellessDrafts,
    tagIds,
    noteCounts: 'notesButton',
  })

  const edges = data?.drafts.edges ?? []
  const pendingFirstLoad =
    (loading && edges.length === 0) || (typeof data === 'undefined' && !error)
  const heldLoading = useDelayedLoading(pendingFirstLoad)
  const renderWithSectionHeading = (content: React.ReactNode): JSX.Element => (
    <>
      {sectionHeading && (
        <VisuallyHidden as="h2">{sectionHeading}</VisuallyHidden>
      )}
      {content}
    </>
  )

  if (pendingFirstLoad || heldLoading) {
    return renderWithSectionHeading(<PostListSkeleton />)
  }

  if (error) {
    return renderWithSectionHeading(<PostListError error={error} />)
  }

  if (!loading && edges.length === 0) {
    return renderWithSectionHeading(
      emptyState ? emptyState : <PostListEmptyState />,
    )
  }

  const lastEdge = edges.at(-1)
  const undatedEdges = edges.filter((edge) => edge.date === null)
  const datedEdges = edges.filter(
    (edge): edge is DatedDraftEdge => edge.date !== null,
  )

  return renderWithSectionHeading(
    <DraftsTimeline
      role="feed"
      aria-busy={fetchingMore}
      data-testid="post-list"
      undated={undatedEdges}
      dated={datedEdges}
      getKey={getDraftEdgeId}
      renderUndated={(edge): JSX.Element => (
        <UndatedDraftEntry
          edge={edge}
          cardRef={edge === lastEdge ? lastElementRef : null}
          showChannelNotices={showChannelNotices}
        />
      )}
      renderDated={(edge): JSX.Element => (
        <DatedDraftEntry
          edge={edge}
          cardRef={edge === lastEdge ? lastElementRef : null}
          showChannelNotices={showChannelNotices}
          isRecurringEnabled={isRecurringEnabled}
          timeZone={timeZone}
        />
      )}
    >
      {data?.drafts.pageInfo.hasNextPage && (
        <PostingTimeline.Loading key="load-more" />
      )}
    </DraftsTimeline>,
  )
}

const UndatedDraftEntry = ({
  edge,
  cardRef,
  showChannelNotices,
}: {
  edge: DraftEdge
  cardRef: CardRef
  showChannelNotices: boolean
}): JSX.Element | null => {
  if (isOfType(edge.contentItemNode, 'ContentItem')) {
    const item = readFragment(ContentItemCard_ContentItem, edge.contentItemNode)
    return (
      <PostingTimeline.Entry>
        <PostingTimeline.TimeLabel status="draft" />
        <PostingTimeline.Content>
          <ContentItemCard contentItem={item} ref={cardRef} />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  if (isOfType(edge.postNode, 'Post')) {
    const post = readFragment(PostCard_Post, edge.postNode)
    return (
      <PostingTimeline.Entry>
        <PostingTimeline.TimeLabel
          status={post.status}
          notificationStatus={post.notificationStatus}
          schedulingType={post.schedulingType}
        />
        <PostingTimeline.Content>
          <PostCard
            post={maskFragments([PostCard_Post], post)}
            ref={cardRef}
            showChannelNotices={showChannelNotices}
            rounded="lg"
          />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  return null
}

const DatedDraftEntry = ({
  edge,
  cardRef,
  showChannelNotices,
  isRecurringEnabled,
  timeZone,
}: {
  edge: DatedDraftEdge
  cardRef: CardRef
  showChannelNotices: boolean
  isRecurringEnabled: boolean
  timeZone: string
}): JSX.Element | null => {
  if (isOfType(edge.contentItemNode, 'ContentItem')) {
    const item = readFragment(ContentItemCard_ContentItem, edge.contentItemNode)
    return (
      <PostingTimeline.Entry>
        <PostingTimeline.TimeLabel status="draft" date={edge.date} />
        <PostingTimeline.Content>
          <ContentItemCard contentItem={item} ref={cardRef} />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  if (isOfType(edge.postNode, 'Post')) {
    const post = readFragment(PostCard_Post, edge.postNode)
    const hasScheduledAt = post.isCustomScheduled || post.isPinned
    const isDraft = post.status === 'draft' || post.status === 'needs_approval'
    const isTentative = hasScheduledAt && isDraft
    const summary =
      post.status === 'scheduled'
        ? getRecurrenceSummary(
            post.recurrence,
            edge.date,
            isRecurringEnabled,
            timeZone,
          )
        : null

    return (
      <PostingTimeline.Entry>
        <PostingTimeline.TimeLabel
          date={edge.date}
          status={post.status}
          notificationStatus={post.notificationStatus}
          schedulingType={post.schedulingType}
          tentative={isTentative}
          overdue={
            isPast(edge.date) && post.status !== 'sent' && post.error === null
          }
          customScheduled={post.isCustomScheduled}
          isPinned={post.isPinned}
          recurring={!!summary}
          recurrenceSummary={summary}
        />
        <PostingTimeline.Content>
          <PostCard
            post={maskFragments([PostCard_Post], post)}
            ref={cardRef}
            showChannelNotices={showChannelNotices}
            rounded="lg"
          />
        </PostingTimeline.Content>
      </PostingTimeline.Entry>
    )
  }

  return null
}
